import { ComponentType } from 'react';

import {
  LoadingSkeleton,
  SeasonPlanPickerSkeleton,
} from '~/features/agronomist/presentation/components/LoadingSkeleton';
import TabContentSkeleton from '~/features/business_admin_list/presentation/components/shared/tab_content_skeleton';
import OrganizationProfileSettingsSkeleton from '~/features/organization_settings/presentation/components/organization_profile_settings_skeleton';
import SeasonCardSkeleton from '~/features/production_head/presentation/components/phSeasonCardSkeleton';
import RolesManagementSkeleton from '~/features/roles_management/presentation/components/roles_management_skeleton';

import DashboardSkeleton from './dashboard_skeleton';
import ProfileSkeleton from './profile_skeleton';
import UserManagementSkeleton from './user_management_skeleton';

// Maps a destination pathname to the skeleton that matches that page's real
// layout, so navigating there shows a shape close to the actual content
// instead of a generic spinner. Routes with no entry here fall back to the
// generic spinner in GlobalLoadingScreen — add an entry as each page gets
// its own skeleton built.
//
// The agronomist/production-head entries below reuse skeleton components
// that already existed and were already rendered in-page for same-route
// data refreshes — this just reuses them for the destination-route case too,
// it doesn't add a new skeleton design.
const routeSkeletonRegistry: { pattern: RegExp; Skeleton: ComponentType }[] = [
  { pattern: /^\/user-management(\/|$)/, Skeleton: UserManagementSkeleton },
  { pattern: /^\/home(\/|$)/, Skeleton: DashboardSkeleton },
  { pattern: /^\/profile(\/|$)/, Skeleton: ProfileSkeleton },
  { pattern: /^\/agronomist-season(\/|$)/, Skeleton: () => <LoadingSkeleton count={6} /> },
  // Scoped to exactly the picker's own path shape (index, :seasonId, or
  // :seasonId/:planId) - same pattern as GlobalLoadingScreen's
  // seasonPlanPickerPattern - so it does NOT apply to version/revision
  // detail pages or the separate create-plan page, which have their own
  // layouts. This only ever renders for a genuine fresh/direct load, since
  // in-flow navigation within the picker skips the full loader entirely.
  {
    pattern: /^\/agronomist-season-plan(?:\/(?!create-plan)[^/]+(?:\/[^/]+)?)?$/,
    Skeleton: SeasonPlanPickerSkeleton,
  },
  { pattern: /^\/production-season(\/|$)/, Skeleton: () => <SeasonCardSkeleton count={4} /> },
  {
    pattern: /^\/organization-profile-settings(\/|$)/,
    Skeleton: OrganizationProfileSettingsSkeleton,
  },
  { pattern: /^\/roles-management(\/|$)/, Skeleton: RolesManagementSkeleton },
  { pattern: /^\/business-admin(\/|$)/, Skeleton: TabContentSkeleton },
];

export function getRouteSkeleton(pathname: string): ComponentType | undefined {
  return routeSkeletonRegistry.find((entry) => entry.pattern.test(pathname))?.Skeleton;
}
